import { Fragment, type CSSProperties, type ReactNode } from "react";
import { Mascot } from "../../components/Mascot";
import { SLIDE_COUNT } from "../../lib/slides";
import { TITLE } from "../../lib/card";
import type { MascotPose } from "../../lib/mascot";
import { Qr } from "./qr";
import "./Slides.css";

/**
 * The talk before round 1 (the `slides.set` event): one slide at a time, full screen, in place
 * of everything else on the wall. Each slide is one colour from edge to edge, with the host on
 * it, large, saying his piece in a bubble: Lord Cockington, who is in charge (decision 45). Only
 * the owner's phone moves them; a round starting ends them.
 */

/** Which ground a slide stands on: a court colour, the accent, or the night (white ink). */
type Ground = "accent" | "court-1" | "court-2" | "court-3" | "court-4" | "night";

interface Slide {
  ground: Ground;
  pose: MascotPose;
  /** The host's size: "huge" on the title and the last slide, "big" beside the words. */
  host: "huge" | "big";
  /** What the host says on this slide, in a bubble over him. */
  says: string;
  body: (link: string | null) => ReactNode;
}

/** Each child of a slide comes in a beat after the one before it. */
const beat = (i: number): CSSProperties => ({ "--i": i } as CSSProperties);

const TIMELINE: [string, string][] = [
  ["1:00", "Arrive, scan the code, tap I'm here"],
  ["1:15", "Round 1"],
  ["Every 15 min", "A new round, a new partner"],
  ["Lunch", "When Abhi calls it. Everything stops"],
  ["The last rounds", "Even out everyone's games"],
  ["5:45", "Last whistle"],
  ["Then", "Lord Cockington's awards, then the podium"],
];

const STEPS = ["Scan the code", "Tap your name and set a 4 number PIN", "Take a picture", "Tap I'm here"];

const ROUND_LINES = [
  "Doubles, a new partner every round",
  "First to 11, win by 2",
  "Up to 13 minutes. Time up: whoever is ahead wins",
  "One of you puts the score in on the court's tablet",
  "Your next court is on the wall. Walk straight there",
];

/**
 * Coming and going as a picture (his ask, 18 Oct): three states in a row with the arrows between
 * them, arrive to playing, playing to and from a break; then going home and lunch as rows.
 */
const FLOW: [string, string][] = [
  ["Arrive", "Tap I'm here. You are in the next draw."],
  ["Playing", "A new court and partner every round. Sit one out now and then."],
  ["On a break", "Tap On a break. Tap I'm back when you are."],
];

const BREAKS: [string, string][] = [
  ["Going home", "Tap Leaving. Your games still count. Back later? Tap I'm here."],
  ["Lunch", "Everything stops. The wall counts down. Be back on court when it says."],
];

const REST: [string, string][] = [
  ["Heckle", "Send a line to the wall"],
  ["Curse", "One a day: wrong hand, no smashes or solo, for 60 seconds"],
  ["Pick", "Call the winners for fun points"],
];

export const SLIDES: Slide[] = [
  {
    ground: "accent",
    pose: "crown",
    host: "huge",
    says: "Silence, peasants. I am Lord Cockington, and today I am in charge.",
    body: () => (
      <div className="sl-title">
        <h1 className="sl-name wall-display sl-in" style={beat(0)}>{TITLE.name}</h1>
        <p className="sl-sub sl-in" style={beat(1)}>{TITLE.sub}</p>
        <p className="sl-when sl-in" style={beat(2)}>{TITLE.when}</p>
        <p className="sl-year wall-display sl-in" style={beat(3)}>{TITLE.year}</p>
      </div>
    ),
  },
  {
    ground: "court-1",
    pose: "calling",
    host: "big",
    says: "Abhi calls lunch. I call everything else.",
    body: () => (
      <>
        <h1 className="sl-head wall-display sl-in" style={beat(0)}>The day</h1>
        <ol className="sl-times">
          {TIMELINE.map(([when, what], i) => (
            <li key={when} className="sl-row sl-in" style={beat(i + 1)}>
              <span className="sl-when-cell wall-display">{when}</span>
              <span className="sl-what-cell">{what}</span>
            </li>
          ))}
        </ol>
      </>
    ),
  },
  {
    ground: "court-2",
    pose: "calling",
    host: "big",
    says: "No picture, no badminton. Yes, even you.",
    body: (link) => (
      <>
        <h1 className="sl-head wall-display sl-in" style={beat(0)}>Getting in</h1>
        <div className="sl-getin">
          <ol className="sl-steps">
            {STEPS.map((step, i) => (
              <li key={step} className="sl-step sl-in" style={beat(i + 1)}>
                <span className="sl-no wall-display">{i + 1}</span>
                <span className="sl-step-text">{step}</span>
              </li>
            ))}
          </ol>
          {link ? (
            <div className="sl-qr-block sl-in" style={beat(5)}>
              <Qr link={link} className="sl-qr" />
            </div>
          ) : null}
        </div>
      </>
    ),
  },
  {
    ground: "court-3",
    pose: "whistle",
    host: "big",
    says: "Win by two. Lose with dignity. One of those is optional.",
    body: () => (
      <>
        <h1 className="sl-head wall-display sl-in" style={beat(0)}>A round</h1>
        <ul className="sl-lines">
          {ROUND_LINES.map((line, i) => (
            <li key={line} className="sl-line sl-in" style={beat(i + 1)}>{line}</li>
          ))}
        </ul>
      </>
    ),
  },
  {
    ground: "court-4",
    pose: "asleep",
    host: "big",
    says: "Take a break. Your partner needs one from you.",
    body: () => (
      <>
        <h1 className="sl-head wall-display sl-in" style={beat(0)}>Coming and going</h1>
        <div className="sl-flow" role="list">
          {FLOW.map(([head, text], i) => (
            <Fragment key={head}>
              {i > 0 && (
                <span className="sl-arrow sl-in" style={beat(i)} aria-label={i === 1 ? "then" : "and back"}>
                  {i === 1 ? "→" : "⇄"}
                </span>
              )}
              <div className="sl-state sl-in" role="listitem" style={beat(i)}>
                <h2 className="sl-block-head wall-display">{head}</h2>
                <p className="sl-block-text">{text}</p>
              </div>
            </Fragment>
          ))}
        </div>
        <div className="sl-blocks">
          {BREAKS.map(([head, text], i) => (
            <div key={head} className="sl-block sl-in" style={beat(i + 3)}>
              <h2 className="sl-block-head wall-display">{head}</h2>
              <p className="sl-block-text">{text}</p>
            </div>
          ))}
        </div>
        <p className="sl-foot sl-in" style={beat(5)}>The draw sorts it out every round. You are ranked on your average, so a break costs you nothing.</p>
      </>
    ),
  },
  {
    ground: "night",
    pose: "smug",
    host: "big",
    says: "Heckle without mercy. I certainly will.",
    body: () => (
      <>
        <h1 className="sl-head wall-display sl-in" style={beat(0)}>While you rest</h1>
        <div className="sl-blocks">
          {REST.map(([head, text], i) => (
            <div key={head} className="sl-block sl-in" data-n={i + 1} style={beat(i + 1)}>
              <h2 className="sl-block-head wall-display">{head}</h2>
              <p className="sl-block-text">{text}</p>
            </div>
          ))}
        </div>
        <p className="sl-foot sl-in" style={beat(4)}>Mini games, a fun board and awards at the end. Five games to make the podium.</p>
      </>
    ),
  },
  {
    ground: "court-1",
    pose: "calling",
    host: "huge",
    says: "I will take questions. I will not answer them.",
    body: () => (
      <div className="sl-title">
        <h1 className="sl-ask wall-display sl-in" style={beat(0)}>Any questions?</h1>
      </div>
    ),
  },
];

if (SLIDES.length !== SLIDE_COUNT) throw new Error("the wall's slides and SLIDE_COUNT disagree");

/** The slide `slide` (0-based) on the whole wall. `link` is the players' link, for the code on "Getting in". */
export function Slides({ slide, link }: { slide: number; link: string | null }) {
  const n = Math.max(0, Math.min(SLIDES.length - 1, slide));
  const s = SLIDES[n]!;
  return (
    <section className="sl" data-ground={s.ground} aria-label={`Slide ${n + 1} of ${SLIDES.length}`}>
      {/* keyed by the slide, so a new one comes in fresh; the ground behind it fades across */}
      <div key={n} className={`sl-stage sl-host-${s.host}`}>
        <div className="sl-body">{s.body(link)}</div>
        <div className="sl-mascot-spot">
          <p className="sl-says sl-in" style={beat(6)}>
            {s.says}
          </p>
          <Mascot pose={s.pose} className="sl-mascot" />
        </div>
      </div>
      <p className="sl-count wall-display">{n + 1} / {SLIDES.length}</p>
    </section>
  );
}

/** ?demo=slides&slide=N (1-based) on the projector: slide N, with no change to the day. Null when not asked for. */
export function demoSlideFromLocation(): number | null {
  const q = new URLSearchParams(location.search);
  if (q.get("demo") !== "slides") return null;
  const n = Number(q.get("slide") ?? 1);
  return Number.isInteger(n) ? Math.max(0, Math.min(SLIDE_COUNT - 1, n - 1)) : 0;
}
